/**
 * Collection Carousel Component
 * Handles horizontal scrolling, arrow navigation, drag support, and keyboard navigation
 */

class CollectionCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.prevButton = null;
    this.nextButton = null;
    this.items = [];
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.dragThreshold = 5; // pixels to move before considering it a drag
    this.hasMoved = false;
    
    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('.collection-carousel__track');
    this.prevButton = this.querySelector('.collection-carousel__arrow--prev');
    this.nextButton = this.querySelector('.collection-carousel__arrow--next');
    this.items = Array.from(this.querySelectorAll('.collection-carousel__item'));
    
    if (!this.track) return;
    
    const enableDrag = this.track.dataset.enableDrag === 'true';
    
    // Arrow buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
    }
    
    // Scroll events
    this.track.addEventListener('scroll', this.handleScroll);
    
    // Keyboard navigation
    this.track.addEventListener('keydown', this.handleKeyDown);
    
    // Drag support (desktop)
    if (enableDrag) {
      this.track.addEventListener('mousedown', this.handleMouseDown);
      this.track.addEventListener('mouseleave', this.handleMouseUp);
      this.track.addEventListener('mouseup', this.handleMouseUp);
      this.track.addEventListener('mousemove', this.handleMouseMove);
    }
    
    // Touch support (mobile)
    if (enableDrag) {
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.track.addEventListener('touchend', this.handleTouchEnd);
    }
    
    // Initial state
    this.updateArrowStates();
    
    // Handle Shopify theme editor
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        this.updateArrowStates();
      });
    }
  }

  disconnectedCallback() {
    if (this.track) {
      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('keydown', this.handleKeyDown);
      this.track.removeEventListener('mousedown', this.handleMouseDown);
      this.track.removeEventListener('mouseleave', this.handleMouseUp);
      this.track.removeEventListener('mouseup', this.handleMouseUp);
      this.track.removeEventListener('mousemove', this.handleMouseMove);
      this.track.removeEventListener('touchstart', this.handleTouchStart);
      this.track.removeEventListener('touchmove', this.handleTouchMove);
      this.track.removeEventListener('touchend', this.handleTouchEnd);
    }
    
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
  }

  handleScroll() {
    this.updateArrowStates();
  }

  updateArrowStates() {
    if (!this.track) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const isAtStart = scrollLeft <= 10;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 10;
    
    if (this.prevButton) {
      this.prevButton.disabled = isAtStart;
    }
    if (this.nextButton) {
      this.nextButton.disabled = isAtEnd;
    }
  }

  handlePrevClick(e) {
    e.preventDefault();
    if (!this.track) return;
    
    const cardWidth = this.getCardWidth();
    const scrollAmount = cardWidth * 2; // Scroll by 2 cards
    
    this.track.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  }

  handleNextClick(e) {
    e.preventDefault();
    if (!this.track) return;
    
    const cardWidth = this.getCardWidth();
    const scrollAmount = cardWidth * 2; // Scroll by 2 cards
    
    this.track.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }

  getCardWidth() {
    if (this.items.length === 0) return 0;
    
    const firstItem = this.items[0];
    const itemStyle = window.getComputedStyle(firstItem);
    const itemWidth = firstItem.offsetWidth;
    const gap = parseFloat(itemStyle.marginRight) || 0;
    
    return itemWidth + gap;
  }

  handleKeyDown(e) {
    if (!this.track) return;
    
    // Only handle arrow keys when track is focused
    if (document.activeElement !== this.track) return;
    
    const cardWidth = this.getCardWidth();
    let scrollAmount = 0;
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        scrollAmount = -cardWidth;
        break;
      case 'ArrowRight':
        e.preventDefault();
        scrollAmount = cardWidth;
        break;
      case 'Home':
        e.preventDefault();
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      case 'End':
        e.preventDefault();
        this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
        return;
      default:
        return;
    }
    
    if (scrollAmount !== 0) {
      this.track.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  handleMouseDown(e) {
    if (!this.track) return;
    
    // Don't start drag if clicking on a link or button
    if (e.target.closest('a, button')) {
      return;
    }
    
    this.isDragging = true;
    this.hasMoved = false;
    this.startX = e.pageX - this.track.offsetLeft;
    this.scrollLeft = this.track.scrollLeft;
    this.track.style.cursor = 'grabbing';
    this.track.style.userSelect = 'none';
    
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging || !this.track) return;
    
    e.preventDefault();
    
    const x = e.pageX - this.track.offsetLeft;
    const walk = (x - this.startX) * 1.5; // Scroll speed multiplier
    const newScrollLeft = this.scrollLeft - walk;
    
    // Check if we've moved enough to consider it a drag
    if (Math.abs(walk) > this.dragThreshold) {
      this.hasMoved = true;
    }
    
    this.track.scrollLeft = newScrollLeft;
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    
    if (this.track) {
      this.track.style.cursor = '';
      this.track.style.userSelect = '';
    }
    
    // If we didn't move much, allow click events
    if (!this.hasMoved && e.target.closest('a')) {
      const link = e.target.closest('a');
      if (link) {
        window.location.href = link.href;
      }
    }
    
    this.hasMoved = false;
  }

  handleTouchStart(e) {
    if (!this.track) return;
    
    // Don't start drag if touching a link or button
    if (e.target.closest('a, button')) {
      return;
    }
    
    this.isDragging = true;
    this.hasMoved = false;
    this.startX = e.touches[0].pageX - this.track.offsetLeft;
    this.scrollLeft = this.track.scrollLeft;
  }

  handleTouchMove(e) {
    if (!this.isDragging || !this.track) return;
    
    const x = e.touches[0].pageX - this.track.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    const newScrollLeft = this.scrollLeft - walk;
    
    // Check if we've moved enough to consider it a drag
    if (Math.abs(walk) > this.dragThreshold) {
      this.hasMoved = true;
      e.preventDefault(); // Prevent scrolling the page
    }
    
    this.track.scrollLeft = newScrollLeft;
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    
    // If we didn't move much, allow tap events
    if (!this.hasMoved && e.target.closest('a')) {
      const link = e.target.closest('a');
      if (link) {
        window.location.href = link.href;
      }
    }
    
    this.hasMoved = false;
  }
}

// Register the custom element
customElements.define('collection-carousel', CollectionCarousel);

// Handle Shopify theme editor reloads
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    // Re-initialize carousels after section reload
    const carousels = document.querySelectorAll('collection-carousel');
    carousels.forEach(carousel => {
      // Re-run initialization if needed
      if (carousel.track) {
        carousel.updateArrowStates();
      }
    });
  });
}
